import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Clock, ChevronRight, Search, Sun, Moon, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';
import Footer from '@/components/Footer';
import axios from 'axios';

const API = `${window.location.origin}/api`;

const CATEGORY_LABELS = {
  tax: 'Car Tax',
  mot: 'MOT',
  buying: 'Buying a Car',
  safety: 'Safety',
  general: 'Guides',
};

const CATEGORY_COLORS = {
  tax: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  mot: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  buying: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  safety: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  general: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

function BlogPage() {
  const { theme, toggleTheme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const activeCategory = searchParams.get('category') || '';

  useEffect(() => {
    fetchData();
  }, [activeCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = activeCategory ? `?category=${activeCategory}` : '';
      const [postsRes, catsRes] = await Promise.all([
        axios.get(`${API}/blog/posts${params}`),
        axios.get(`${API}/blog/categories`),
      ]);
      setPosts(postsRes.data.posts);
      setCategories(catsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const setCategory = (cat) => {
    if (cat === activeCategory) {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  const extractFirstParagraph = (content) => {
    const lines = content.split('\n').filter(l => l.trim() && !l.startsWith('#') && !l.startsWith('|') && !l.startsWith('-') && !l.startsWith('['));
    return lines[0]?.trim()?.slice(0, 180) + '...' || '';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 transition-colors">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://customer-assets.emergentagent.com/job_carinfo-lookup/artifacts/kwmtzzvb_image.png"
              alt="IsItTaxed.co.uk"
              className="h-12 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link to="/">
              <Button size="sm" className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold" data-testid="check-vehicle-btn">
                Check a Vehicle
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-10">
          <Link to="/" className="text-gray-400 dark:text-gray-500 text-sm hover:text-gray-600 dark:hover:text-gray-300 transition-colors inline-flex items-center gap-1 mb-4">
            <ArrowLeft className="h-3 w-3" /> Back to home
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="blog-heading">
            Guides & Advice
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            Everything UK drivers need to know about tax, MOT, buying used cars, and keeping your vehicle legal.
          </p>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="category-filters">
          <button
            onClick={() => setCategory('')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${!activeCategory ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
            data-testid="filter-all"
          >
            All
          </button>
          {categories.map(({ category, count }) => (
            <button
              key={category}
              onClick={() => setCategory(category)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${activeCategory === category ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
              data-testid={`filter-${category}`}
            >
              {CATEGORY_LABELS[category] || category} ({count})
            </button>
          ))}
        </div>

        {/* Posts */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto" />
          </div>
        ) : (
          <div className="space-y-4" data-testid="blog-posts-list">
            {posts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`}>
                <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 transition-all cursor-pointer group mb-4" data-testid={`blog-card-${post.slug}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[post.category] || CATEGORY_COLORS.general}`}>
                            {CATEGORY_LABELS[post.category] || post.category}
                          </span>
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors mb-1">
                          {post.title}
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 text-sm line-clamp-2">
                          {post.meta_description || extractFirstParagraph(post.content)}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-300 dark:text-gray-600 group-hover:text-yellow-500 transition-colors mt-1 shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default BlogPage;
